import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ComplianceButton } from '@/components/lumen/ComplianceButton';
import { NAV_GROUPS, NAV_LABEL, siteScopeOf, plantScopeOf } from '@/app/nav';
import { ViewErrorBoundary } from '@/app/ErrorBoundary';
import { LumenSetPassword, LumenSignIn, type SignInEntry } from '@/modules/auth/signin/LumenSignIn';
import { readAuthLink } from '@/modules/auth/auth-fragment';
import { AuthProvider } from '@/lib/auth';
import { useAuth } from '@/lib/auth-context';
import { useDeviceCounts } from '@/lib/api/onboarding';
import { useFindingsSummary, useSite } from '@/lib/api/sites';
import { useMe } from '@/lib/api/me';
import { useWriteRequests } from '@/lib/api/writes';
import { SiteProvider } from './site-context';
import { PlantProvider } from './plant-context';
import { OrgProvider } from './org-context';
import { useSiteContext } from './site-context-value';
import { HeaderScope } from '@/components/lumen/HeaderScope';
import { FindingsButton } from '@/components/lumen/FindingsButton';
import { AccountMenu } from '@/components/lumen/AccountMenu';
import { AlarmBell, AlarmToasts } from '@/components/lumen/AlarmToasts';
import { Toasts } from '@/components/lumen/Toasts';
import { useAlarmFeed } from './alarm-feed';
import { useAlarmSummary } from '@/lib/api/queries';
import { useAlarmStream } from '@/lib/api/events';
import { Providers } from './providers';
import { Page, Shell } from './Shell';
import { BrandLoader } from '@/components/lumen/BrandLoader';
import { CustomerBrand } from '@/components/lumen/CustomerBrand';
import { MyWork } from '@/components/lumen/MyWork';
import { useBootHold } from '@/lib/use-boot-hold';
import { Icon } from '@/components/lumen/Icon';
import { StatusBadge } from '@/components/lumen/StatusBadge';
import { needsOf, PRODUCT } from '@/product/profile';
import { notShipped } from '@/product/shipped';
import { useWorkNav } from './work-nav';
/* Type-only, and erased: the screens' shapes, without putting their modules in the graph. */
import type * as AnalysisViewModule from '@/modules/analysis/AnalysisView';
import type * as WorkViewModule from '@/modules/work/WorkView';
import type * as FleetReportsViewModule from '@/modules/fleet-reports/ReportsView';
import type * as WatchlistDrawerModule from '@/modules/watchlist/WatchlistDrawer';
import type * as SearchPaletteModule from '@/modules/search/SearchPalette';
import type * as AssetsViewModule from '@/modules/assets/AssetsView';
import type * as LiveAlertsDrawerModule from '@/modules/command/LiveAlertsDrawer';
import { LiveAlertsButton } from '@/components/lumen/LiveAlertsButton';
import { WatchlistButton } from '@/components/lumen/WatchlistButton';
import { SearchButton } from '@/components/lumen/SearchButton';
import type { SearchTarget } from '@/modules/search/search';
import type { Queue } from '@/modules/alarms/queues';
import type * as CabinetOnboardingViewModule from '@/modules/cabinets/CabinetOnboardingView';
import type * as CabinetViewModule from '@/modules/cabinets/CabinetView';
import type * as CabinetsViewModule from '@/modules/cabinets/CabinetsView';
import type * as CalculatorsViewModule from '@/modules/calculators/CalculatorsView';
import type * as CommandCentreViewModule from '@/modules/command/CommandCentreView';
import type * as HomeViewModule from '@/modules/home/HomeView';
import type * as SettingsViewModule from '@/modules/settings/SettingsView';
import type * as AccountMenuFescoModule from '@/components/lumen/AccountMenuFesco';
import type * as ProfileManagementViewModule from '@/modules/profiles/ProfileManagementView';
import type * as DesignSystemViewModule from '@/modules/design/DesignSystemView';
import type * as AppearanceViewModule from '@/modules/appearance/AppearanceView';
import type * as ComplianceStageModule from '@/modules/compliance/ComplianceStage';
import type * as FindingsViewModule from '@/modules/findings/FindingsView';
import type * as SiteOnboardingViewModule from '@/modules/onboarding/SiteOnboardingView';
import type * as OverviewViewModule from '@/modules/overview/OverviewView';
import type * as ReportsViewModule from '@/modules/reports/ReportsView';
import type * as StandardsViewModule from '@/modules/standards/StandardsView';
import type * as SldStudioViewModule from '@/modules/topology/SldStudioView';
import type * as DigitalTwinViewModule from '@/modules/twin/DigitalTwinView';

/*
 * EVERY MODULE VIEW IS A SEPARATE CHUNK.
 *
 * The whole console used to ship as one 4.30 MB file (1.26 MB gzipped), so an operator
 * downloaded Mapbox GL and Highcharts — the two heaviest things here by far — before the
 * SIGN-IN screen could paint, on every cold load, whether or not they ever opened a map
 * or a chart. Splitting at the view boundary rather than at each library's call site is
 * what makes that automatic: Mapbox follows the views that draw a map, Highcharts follows
 * the views that draw a chart, and neither follows anyone else.
 *
 * The sign-in screen and the shell are deliberately NOT lazy: they are the first paint,
 * and a chunk boundary there would trade the problem for a blank frame.
 *
 * `.then(m => ({ default: m.X }))` because these are named exports and React.lazy resolves
 * a module's `default`.
 *
 * A screen only one product offers is imported behind `__PRODUCT__` (`src/product/shipped.ts`): the
 * other product's build folds the test away with the `import()`, and never emits that chunk.
 */
const ComplianceStage = lazy(() =>
  (__PRODUCT__ === 'thunder' ? import('@/modules/compliance/ComplianceStage') : notShipped<typeof ComplianceStageModule>()).then((m) => ({ default: m.ComplianceStage })),
);
const OverviewView = lazy(() =>
  (__PRODUCT__ === 'thunder' ? import('@/modules/overview/OverviewView') : notShipped<typeof OverviewViewModule>()).then((m) => ({ default: m.OverviewView })),
);
const CommandCentreView = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/command/CommandCentreView') : notShipped<typeof CommandCentreViewModule>()).then((m) => ({ default: m.CommandCentreView })),
);
const HomeView = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/home/HomeView') : notShipped<typeof HomeViewModule>()).then((m) => ({ default: m.HomeView })),
);
/* Cratus's Your account is the FESCO design's Settings; Thunder keeps ProfileView. */
const SettingsView = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/settings/SettingsView') : notShipped<typeof SettingsViewModule>()).then((m) => ({ default: m.SettingsView })),
);
/* Cratus's account menu is the FESCO design's (name, role, theme and glass inside it); Thunder
   keeps AccountMenu. */
const AccountMenuFesco = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/components/lumen/AccountMenuFesco') : notShipped<typeof AccountMenuFescoModule>()).then((m) => ({
    default: m.AccountMenuFesco,
  })),
);
const ProfileManagementView = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/profiles/ProfileManagementView') : notShipped<typeof ProfileManagementViewModule>()).then((m) => ({
    default: m.ProfileManagementView,
  })),
);
const DesignSystemView = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/design/DesignSystemView') : notShipped<typeof DesignSystemViewModule>()).then((m) => ({ default: m.DesignSystemView })),
);
const AppearanceView = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/appearance/AppearanceView') : notShipped<typeof AppearanceViewModule>()).then((m) => ({ default: m.AppearanceView })),
);
const IntegrationsView = lazy(() => import('@/modules/manage/IntegrationsView').then((m) => ({ default: m.IntegrationsView })));
const DeviceManagementView = lazy(() => import('@/modules/manage/DeviceManagementView').then((m) => ({ default: m.DeviceManagementView })));
const FindingsView = lazy(() =>
  (__PRODUCT__ === 'thunder' ? import('@/modules/findings/FindingsView') : notShipped<typeof FindingsViewModule>()).then((m) => ({ default: m.FindingsView })),
);
const SiteOnboardingView = lazy(() =>
  (__PRODUCT__ === 'thunder' ? import('@/modules/onboarding/SiteOnboardingView') : notShipped<typeof SiteOnboardingViewModule>()).then((m) => ({ default: m.SiteOnboardingView })),
);
const SiteHubView = lazy(() => import('@/modules/sites/SiteHubView').then((m) => ({ default: m.SiteHubView })));
const SitesView = lazy(() => import('@/modules/sites/SitesView').then((m) => ({ default: m.SitesView })));
const CabinetsView = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/cabinets/CabinetsView') : notShipped<typeof CabinetsViewModule>()).then((m) => ({ default: m.CabinetsView })),
);
const CabinetView = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/cabinets/CabinetView') : notShipped<typeof CabinetViewModule>()).then((m) => ({ default: m.CabinetView })),
);
const CabinetOnboardingView = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/cabinets/CabinetOnboardingView') : notShipped<typeof CabinetOnboardingViewModule>()).then((m) => ({ default: m.CabinetOnboardingView })),
);
const StandardsView = lazy(() =>
  (__PRODUCT__ === 'thunder' ? import('@/modules/standards/StandardsView') : notShipped<typeof StandardsViewModule>()).then((m) => ({ default: m.StandardsView })),
);
const CalculatorsView = lazy(() =>
  (__PRODUCT__ === 'thunder' ? import('@/modules/calculators/CalculatorsView') : notShipped<typeof CalculatorsViewModule>()).then((m) => ({ default: m.CalculatorsView })),
);
const SldStudioView = lazy(() =>
  (__PRODUCT__ === 'thunder' ? import('@/modules/topology/SldStudioView') : notShipped<typeof SldStudioViewModule>()).then((m) => ({ default: m.SldStudioView })),
);
const AnalysisView = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/analysis/AnalysisView') : notShipped<typeof AnalysisViewModule>()).then((m) => ({ default: m.AnalysisView })),
);
const LiveAlertsDrawer = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/command/LiveAlertsDrawer') : notShipped<typeof LiveAlertsDrawerModule>()).then((m) => ({
    default: m.LiveAlertsDrawer,
  })),
);
const WorkView = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/work/WorkView') : notShipped<typeof WorkViewModule>()).then((m) => ({ default: m.WorkView })),
);
const WatchlistDrawer = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/watchlist/WatchlistDrawer') : notShipped<typeof WatchlistDrawerModule>()).then((m) => ({
    default: m.WatchlistDrawer,
  })),
);
const SearchPalette = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/search/SearchPalette') : notShipped<typeof SearchPaletteModule>()).then((m) => ({
    default: m.SearchPalette,
  })),
);
const AssetsView = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/assets/AssetsView') : notShipped<typeof AssetsViewModule>()).then((m) => ({ default: m.AssetsView })),
);
const FleetReportsView = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/fleet-reports/ReportsView') : notShipped<typeof FleetReportsViewModule>()).then((m) => ({ default: m.ReportsView })),
);
const TelemetryView = lazy(() => import('@/modules/telemetry/TelemetryView').then((m) => ({ default: m.TelemetryView })));
const DigitalTwinView = lazy(() =>
  (__PRODUCT__ === 'thunder' ? import('@/modules/twin/DigitalTwinView') : notShipped<typeof DigitalTwinViewModule>()).then((m) => ({ default: m.DigitalTwinView })),
);
const AlarmsView = lazy(() => import('@/modules/alarms/AlarmsView').then((m) => ({ default: m.AlarmsView })));
const ReportsView = lazy(() =>
  (__PRODUCT__ === 'thunder' ? import('@/modules/reports/ReportsView') : notShipped<typeof ReportsViewModule>()).then((m) => ({ default: m.ReportsView })),
);
/* Cratus shows the FESCO design's User Access Control over the same rules (`modules/admin/uac`);
   Thunder keeps Users & access. Either build loads only its own. */
const UsersView = lazy(() =>
  __PRODUCT__ === 'cratus'
    ? import('@/modules/admin/uac/UacView').then((m) => ({ default: m.UacView }))
    : import('@/modules/admin/UsersView').then((m) => ({ default: m.UsersView })),
);
const AccessView = lazy(() => import('@/modules/admin/AccessView').then((m) => ({ default: m.AccessView })));
const PerformanceView = lazy(() =>
  import('@/modules/performance/PerformanceView').then((m) => ({ default: m.PerformanceView })),
);
/* Cratus shows the FESCO design's Audit Trail over the same trail (`modules/admin/audit`); Thunder
   keeps its audit view. Either build loads only its own. */
const AuditView = lazy(() =>
  __PRODUCT__ === 'cratus'
    ? import('@/modules/admin/audit/AuditTrailView').then((m) => ({ default: m.AuditTrailView }))
    : import('@/modules/admin/AuditView').then((m) => ({ default: m.AuditView })),
);
const OrganisationView = lazy(() =>
  import('@/modules/admin/OrganisationView').then((m) => ({ default: m.OrganisationView })),
);
const ProfileView = lazy(() => import('@/modules/admin/ProfileView').then((m) => ({ default: m.ProfileView })));
const RemoteControlView = lazy(() => import('@/modules/writes/RemoteControlView').then((m) => ({ default: m.RemoteControlView })));

/**
 * Application root.
 *
 * The auth gate is OUTSIDE the shell on purpose. Rendering the sidebar and then filling
 * every panel with an error looks like the product is broken rather than that nobody is
 * signed in.
 *
 * Navigation is still `tab` state rather than TanStack Router. Known gap: it costs
 * shareable URLs — a link to one site's device queue, or to a filtered findings view —
 * which is what ADR-0005's search-param schemas are for. Deliberately not bundled with
 * the module work, because converting the shell touches every screen at once.
 *
 * One consequence of that gap is load-bearing here: because the shell owns the whole URL,
 * a route that has to work BEFORE sign-in cannot be a tab. `PUBLIC_ROUTES` below is that
 * second, pre-auth list, and `/accept-invite` is its first member.
 */
export function App(): ReactNode {
  return (
    <Providers>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </Providers>
  );
}

/**
 * THE AUTH GATE, WHICH NOW ALSO ROUTES AN INVITATION
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BELT AND BRACES: TWO INDEPENDENT REASONS TO DEMAND A PASSWORD
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `mustChangePassword` reads `app_metadata.must_change_password`, which the API sets when
 * it issues the invite. That is the primary mechanism and it is the right one — it survives
 * a reload, a different browser, and a user who bookmarks the console.
 *
 * It is not sufficient on its own. It is a WRITE, on the server, at invite time; if it
 * failed or was skipped, an invited user arriving on a link would be dropped straight onto
 * the console having never chosen a password — a credential that is still the one from the
 * email, and therefore still shared, which is precisely what makes an audit trail
 * unattributable. So `type=invite` on the link is honoured as well, independently.
 *
 * Held in state rather than read during render: the intent has to be forgettable. Once the
 * password is set, `LumenSetPassword` reports back and the gate lets go — a module-level
 * read would hold the operator on that screen until they reloaded.
 */
function Gate(): ReactNode {
  const { loading } = useAuth();
  /* The loader is held for a minimum on a cold load, then fades over the page it uncovers. The
     page underneath is mounted while it fades, which is why it can fade rather than cut. */
  const boot = useBootHold(loading);
  if (boot === 'holding') return <Splash />;
  return (
    <>
      <GateContent />
      {boot === 'leaving' && <Splash leaving />}
    </>
  );
}

function GateContent(): ReactNode {
  const { session, loading, mustChangePassword } = useAuth();
  const [linkIntent, setLinkIntent] = useState<'invite' | 'recovery' | null>(linkIntentOnLoad);

  /*
   * THE LINK'S INTENT DIES WITH THE SESSION IT ARRIVED ON.
   *
   * Without this, someone who followed a recovery link, decided not to change their password
   * and signed out was trapped: `linkIntent` outlived the session, so signing back in with
   * their existing, perfectly good password put them straight back on the set-password
   * screen — `mustChangePassword` false, `linkIntent` still 'recovery' — and the only ways
   * out were to set a password or to loop through sign-out again.
   *
   * An intent is a fact about ONE arrival. When the session it came with ends, so does it.
   */
  useEffect(() => {
    if (session === null) setLinkIntent(null);
  }, [session]);

  // Hold the frame until the persisted session resolves; otherwise a reload flashes the
  // login page for anyone already signed in. (`Gate` has held it already on a cold load; this
  // covers a session that is read again later.)
  if (loading) return <Splash />;
  if (!session) {
    /*
     * No session, but possibly a reason to be here anyway: the invitation landing route, or
     * a link that GoTrue refused. Both need the code entry rather than a password form —
     * the person has no password yet, so the password form is the one screen that cannot
     * help them.
     */
    const entry = signInEntry();
    return <LumenSignIn {...(entry ? { entry } : {})} onIntent={setLinkIntent} />;
  }
  if (mustChangePassword || linkIntent) {
    /* `reset` only when the LINK said recovery. A `must_change_password` flag means the
       credential was issued, which is the stronger claim and the one to print. */
    return (
      <LumenSetPassword
        onDone={() => setLinkIntent(null)}
        reason={!mustChangePassword && linkIntent === 'recovery' ? 'reset' : 'issued'}
      />
    );
  }
  return (
    /* OUTERMOST OF THE THREE SCOPES. Organisation → Site → Plant, and each resets the ones
       inside it — a site id means nothing under a different organisation, exactly as a plant id
       means nothing under a different site. It also decides where WRITES go, which is why it
       wraps everything rather than sitting beside the others. */
    <OrgProvider>
      <SiteProvider>
        {/* Inside the site provider, because a plant is a part of the SELECTED site and the
            selection resets whenever that site changes. Renders nothing for a site whose
            topology does not divide, which is both live sites today. */}
        <PlantProvider>
          <AppInner />
        </PlantProvider>
      </SiteProvider>
    </OrgProvider>
  );
}

/** `magiclink` is deliberately absent: it signs you in, it does not oblige a new password. */
function linkIntentOnLoad(): 'invite' | 'recovery' | null {
  const link = readAuthLink();
  if (link?.kind !== 'session') return null;
  return link.type === 'invite' || link.type === 'recovery' ? link.type : null;
}

/**
 * Why the sign-in frame should open on the code screen rather than the password form.
 *
 * Two ways in, and the second is the one that matters: a link that FAILED. An expired or
 * already-spent invitation has to say so and offer the code from the same email, because
 * the alternative — the ordinary sign-in form — asks for a password the person does not
 * have and never had.
 *
 * The PATH decides which email the person is holding when the fragment cannot. GoTrue's
 * error redirect carries `error_code` and no `type`, so for a failed link the landing route
 * is the only evidence left: invitations arrive at `/accept-invite`, resets at `/`.
 */
function signInEntry(): SignInEntry | null {
  const invited = publicRouteFromLocation() === 'accept-invite';
  const link = readAuthLink();

  if (link?.kind === 'failure') {
    /*
     * ONLY THREE TYPES HAVE A CODE SCREEN, and the other three used to be answered as if
     * they were a password reset.
     *
     * `parseAuthLink` also returns `signup`, `email_change` and `email`. A failed link of
     * any of those fell through to the `invited ? 'invite' : 'recovery'` default, so a
     * broken email-change confirmation told the reader to "Enter your reset code" — for a
     * reset they never requested, from an email that contains no such code.
     *
     * Those three are mapped to `null` here, and a null purpose means the code screen has
     * nothing to offer: `signInEntry` returns the failure NOTICE without a purpose, so the
     * reader is told the link did not work on the ordinary sign-in screen rather than being
     * sent to a form that cannot help them. None of the three is reachable in the shipped
     * product today (see the email templates' README), which is exactly why a wrong answer
     * here would have gone unnoticed.
     */
    const stated =
      link.type === 'invite' || link.type === 'recovery' || link.type === 'magiclink'
        ? link.type
        : null;
    if (link.type === 'signup' || link.type === 'email_change' || link.type === 'email') {
      /* No purpose: the sign-in screen shows the failure and stays put, because there is no
         code screen that could help and pretending otherwise is what produced "Enter your
         reset code" for a broken email-change confirmation. */
      return { purpose: null, notice: link.message };
    }
    /*
     * `otp_expired` and `access_denied` mean the CREDENTIAL is spent, not just the link —
     * GoTrue issues one per email and the six-digit code is that same credential. A
     * malformed or truncated link is different: the code in the email is still good, which
     * is the case the code screen was written for.
     */
    const expired = link.code === 'otp_expired' || link.code === 'access_denied';
    return {
      purpose: stated ?? (invited ? 'invite' : 'recovery'),
      notice: link.message,
      expired,
    };
  }

  return invited ? { purpose: 'invite' } : null;
}

/**
 * While the session is read, and for the minimum a cold load shows the loader (`useBootHold`): the
 * product's animated mark, in the same place and size as the splash `index.html` paints before any
 * script runs, and in step with it (`lib/loader-clock.ts`), so the two read as one loader.
 * `leaving` fades it away over the page beneath.
 */
function Splash({ leaving = false }: { leaving?: boolean }): ReactNode {
  return (
    <div className="lm-boot" style={{ background: 'var(--bg)' }} {...(leaving ? { 'data-leaving': '' } : {})}>
      <BrandLoader size={64} label={`Loading ${PRODUCT.name}`} />
    </div>
  );
}

type Tab =
  /** Cratus's landing: the module tiles, with no rail (`PRODUCT.sidebar`). Thunder hides it. */
  | 'home'
  /** Cratus's fleet board (`src/product/`). Every other product hides it. */
  | 'command'
  | 'overview' | 'twin' | 'findings' | 'telemetry' | 'alarms'
  /** Cratus's analysis of a window (CRATUS 2.0 §4). Every other product hides it. */
  | 'analysis'
  /** Work orders and maintenance (Lumen-BE 0092, roadmap Phase 2). Every other product hides it. */
  | 'work'
  /** Cratus's operational reports (roadmap §11). Thunder's `reports` are compliance reports; it hides this. */
  | 'fleet-reports'
  /** Cratus's portfolio and fleet health (roadmap §12). Every other product hides it. */
  | 'assets'
  | 'sites' | 'onboarding' | 'integrations' | 'devices' | 'topology' | 'calculators'
  /** Cratus's cabinets, one cabinet, and the form that onboards one. Every other product hides them. */
  | 'cabinets' | 'cabinet' | 'cabinet-onboarding'
  /** Remote control (ADR-0014). Routable only; the rail hides it without the capability. */
  | 'writes'
  /* `metrics` is a SUBPAGE of Device management now, but it stays routable: it was a rail
     entry, so bookmarks and the Findings deep-links point at it. */
  | 'metrics'
  | 'standards' | 'compliance' | 'reports'
  | 'users' | 'performance' | 'access' | 'audit' | 'organisation' | 'profile'
  /** Cratus's Profile Management (the FESCO design). Thunder hides it. */
  | 'profiles'
  /** Cratus's Design System explorer. Thunder hides it. */
  | 'design'
  /** Cratus's Single Site Center: the selected site's hub as a destination. Thunder hides it. */
  | 'site-center'
  /** Cratus's Appearance configurator. Thunder hides it. */
  | 'appearance'
  /** Launched from Sites rather than a nav destination. */
  | 'site-detail';

/**
 * Every tab id, as a runtime value. `Tab` is a type and types do not survive to runtime, so a
 * URL segment has to be validated against this. Kept adjacent to the union above: adding a tab
 * without adding it here means that tab is reachable by clicking and NOT by URL, which is the
 * kind of asymmetry nobody notices until they bookmark it.
 */
const TABS: readonly Tab[] = [
  'home',
  'command',
  'overview', 'twin', 'findings', 'telemetry', 'alarms',
  'analysis',
  'work',
  'fleet-reports',
  'assets',
  'sites', 'onboarding', 'integrations', 'devices', 'metrics', 'topology', 'calculators',
  'cabinets', 'cabinet', 'cabinet-onboarding',
  'writes',
  'standards', 'compliance', 'reports',
  'users', 'performance', 'access', 'audit', 'organisation', 'profile',
  'profiles',
  'design',
  'site-center',
  'appearance',
  'site-detail',
];

/** Where the product opens, and where a hidden or unknown tab lands — Overview for Thunder,
 *  Home for Cratus. See `src/product/profile.ts`. */
const DEFAULT_TAB: Tab = PRODUCT.homeTab;

/**
 * The tab named by the current URL, or the default when the URL names nothing valid.
 *
 * A tab this PRODUCT does not offer is refused here, at parse time, rather than left to the
 * `hiddenItems` effect: that effect waits for `/me`, so a bookmarked `/compliance` on Cratus
 * would render the Compliance screen — and fire its requests — for a moment before bouncing.
 * A product's hidden set needs no answer from the server, so nothing has to wait for one.
 */
function tabFromLocation(): Tab {
  if (typeof window === 'undefined') return DEFAULT_TAB;
  const seg = window.location.pathname.replace(/^\/+|\/+$/g, '');
  return (TABS as readonly string[]).includes(seg) && !PRODUCT.hiddenTabs.has(seg)
    ? (seg as Tab)
    : DEFAULT_TAB;
}

/**
 * The cabinet `/cabinet?id=` names, or null for the viewer's default.
 *
 * The one tab whose subject is in the URL from the shell rather than from the view. Two of its
 * three ways in (the Cabinets list and the Command Centre) are other screens handing over a
 * cabinet, which is the shell's job, as `alarmFocus` is. A bookmark has to reopen the same
 * cabinet, so the id is carried in the URL too.
 */
function cabinetFromLocation(): string | null {
  if (typeof window === 'undefined' || window.location.pathname.replace(/^\/+|\/+$/g, '') !== 'cabinet') {
    return null;
  }
  const id = new URLSearchParams(window.location.search).get('id');
  return id !== null && id.trim() !== '' ? id : null;
}

/**
 * The alarm `/alarms?id=` names — the link a reminder mail carries (Lumen-BE 0091) — or null. Read
 * once on load and handed to the board, which opens it in the drawer.
 */
function alarmFromLocation(): string | null {
  if (typeof window === 'undefined' || window.location.pathname.replace(/^\/+|\/+$/g, '') !== 'alarms') return null;
  const id = new URLSearchParams(window.location.search).get('id');
  return id !== null && /^\d{1,19}$/.test(id) ? id : null;
}

/** The work order `/work?id=` names — the link a work mail carries (Lumen-BE 0092) — or null. */
function workFromLocation(): string | null {
  if (typeof window === 'undefined' || window.location.pathname.replace(/^\/+|\/+$/g, '') !== 'work') return null;
  const id = new URLSearchParams(window.location.search).get('id');
  return id !== null && /^[0-9a-f-]{36}$/i.test(id) ? id : null;
}

/** The cabinet the onboarding form is EDITING, carried as `/cabinet-onboarding?edit=<id>` for the same reason. */
function cabinetEditFromLocation(): string | null {
  if (typeof window === 'undefined' || window.location.pathname.replace(/^\/+|\/+$/g, '') !== 'cabinet-onboarding') {
    return null;
  }
  const id = new URLSearchParams(window.location.search).get('edit');
  return id !== null && id.trim() !== '' ? id : null;
}

/**
 * Routes that must work with NO SESSION, listed as a runtime value for the same reason
 * `TABS` is: a type does not survive to runtime, so a URL segment has to be validated
 * against something.
 *
 * The asymmetry the `TABS` note warns about is worse here, not milder. A tab left out of
 * `TABS` is still reachable by clicking; a route left out of THIS list is reachable by
 * nothing at all, because arriving by URL is the only way anyone ever arrives — it is the
 * address printed in an email. `/accept-invite` is where the API now points invitations, so
 * it is here, and it must stay outside `TABS`: a segment in both would be claimed by the
 * shell and rendered as a `Stub` inside a console the invited user cannot yet reach.
 */
const PUBLIC_ROUTES = ['accept-invite'] as const;
type PublicRoute = (typeof PUBLIC_ROUTES)[number];

function publicRouteFromLocation(): PublicRoute | null {
  if (typeof window === 'undefined') return null;
  const seg = window.location.pathname.replace(/^\/+|\/+$/g, '');
  return (PUBLIC_ROUTES as readonly string[]).includes(seg) ? (seg as PublicRoute) : null;
}

function AppInner(): ReactNode {
  /*
   * THE TAB LIVES IN THE URL, and this is a bug fix rather than a feature.
   *
   * It was `useState<Tab>('overview')` with no URL sync of any kind, so the address bar never
   * changed and every REFRESH threw the operator back to Overview — from the middle of a
   * telemetry selection, a half-drawn diagram, or a findings queue they were working through.
   * Browser back and forward did nothing at all for the same reason, and no view in the product
   * could be linked to or bookmarked.
   *
   * `history.pushState` rather than a router: this app has one level of navigation and a
   * fifteen-value union, so a router would be more moving parts than the problem has. The
   * `popstate` listener is what makes the browser's own back/forward work, and reading the URL
   * in the `useState` initialiser is what makes a refresh land where the operator was.
   */
  const [tab, setTab] = useState<Tab>(tabFromLocation);
  /* The serial the cabinet form just onboarded, for the list to say so once. */
  const [justOnboarded, setJustOnboarded] = useState<string | null>(null);

  /**
   * WHICH ROWS THE AUDIT TRAIL SHOULD OPEN ON.
   *
   * The three Administer screens answer one question between them — who has access, what
   * happened to it, and what is mine — and they could not reach each other. A demotion is a
   * decision about behaviour, and the evidence for it sat one tab away with no way to get
   * from a person to their rows: the reader had to switch tabs and re-pick the same name out
   * of a dropdown. Held here, beside `tab`, for the same reason `selectedId` is: it is
   * shell-level navigation state, not something a page owns.
   *
   * Consumed ONCE. The trail clears it after applying it, so pressing Audit again later
   * shows the whole trail rather than silently re-applying a filter from ten minutes ago.
   */
  const [auditFocus, setAuditFocus] = useState<
    { actorId?: string; entityType?: string; entityId?: string } | null
  >(null);

  const openAuditFor = (filter: { actorId?: string; entityType?: string; entityId?: string }) => {
    setAuditFocus(filter);
    setTab('audit');
  };

  /*
   * The same pattern one axis over: a breakdown row on the Overview opens the Alarms board
   * on the device it names. Held here beside `tab` for the reason `auditFocus` is — it is
   * shell-level navigation state, not something either page owns — and consumed ONCE, so
   * returning to Alarms later shows the whole board rather than silently re-applying a
   * filter from ten minutes ago.
   */
  const [alarmFocus, setAlarmFocus] = useState<{ deviceId?: number; queue?: Queue } | null>(null);
  /* One alarm a link opens (`alarmFromLocation`), consumed once like `alarmFocus`. */
  const [alarmLink, setAlarmLink] = useState<string | null>(alarmFromLocation);

  /* The cabinet the Single Cabinet View shows; see `cabinetFromLocation`. */
  const [cabinetId, setCabinetId] = useState<string | null>(cabinetFromLocation);
  /* The sensor an alarm link is about, applied once by the cabinet view, like `alarmFocus`. */
  const [cabinetSensor, setCabinetSensor] = useState<string | null>(null);
  const openCabinet = (id: string, sensor: string | null = null) => {
    setCabinetId(id);
    setCabinetSensor(sensor);
    setTab('cabinet');
  };
  /* Cabinets handed to Analysis — by the Single Cabinet View or a site's Analytics — applied once like `cabinetSensor`. */
  const [analysisCabinets, setAnalysisCabinets] = useState<string[] | null>(null);
  const openAnalysis = (cabinetIds: string[]) => {
    setAnalysisCabinets(cabinetIds);
    setTab('analysis');
  };
  /* The cabinet the onboarding form edits; null onboards a new one. */
  const [editCabinetId, setEditCabinetId] = useState<string | null>(cabinetEditFromLocation);

  /*
   * Compliance is site-scoped and reads the header's selection, so the site is chosen HERE
   * before the tab changes — a navigation that skipped it would open the matrix on whatever
   * site was already selected and show the wrong plant's verdicts under the right heading.
   * The Exposure cell selects too; doing it in both places is deliberate belt and braces,
   * and `select` is idempotent.
   */
  const openComplianceFor = (siteId: string) => {
    selectSite(siteId);
    setTab('compliance');
  };

  const openAlarmsFor = (focus: { deviceId?: number; queue?: Queue }) => {
    setAlarmFocus(Object.keys(focus).length > 0 ? focus : null);
    setTab('alarms');
  };

  /* Where a search result goes: each to its own screen, as the screens themselves hand over. */
  const openSearchResult = (t: SearchTarget) => {
    if (t.kind === 'cabinet') openCabinet(t.id);
    else if (t.kind === 'site') {
      selectSite(t.id);
      setTab('site-detail');
    } else if (t.kind === 'alarm') {
      setAlarmLink(t.id);
      setTab('alarms');
    } else if (t.kind === 'work') useWorkNav.getState().open(t.id);
    else setTab('users');
  };

  /*
   * Work (Lumen-BE 0092): a work order a link names, or what another screen asked Work to open —
   * an alarm's or a cabinet's draft, a work order from My work (`work-nav.ts`).
   */
  const [workLink, setWorkLink] = useState<string | null>(workFromLocation);
  const workRequest = useWorkNav((s) => s.request);
  /* Live alerts on every screen (CRATUS 2.0 #26): the NOC's list in a drawer. Cratus only. */
  const [liveOpen, setLiveOpen] = useState(false);
  /* The watchlist drawer (Lumen-BE 0095), opened from the header like Live alerts. */
  const [watchOpen, setWatchOpen] = useState(false);
  /* Global search (roadmap §11.6): the palette, from the header, `/` or Ctrl-K. */
  const [searchOpen, setSearchOpen] = useState(false);
  /* Which Settings section the account menu asked for; `n` remounts Settings when it is asked
     again while already open. */
  const [settingsAt, setSettingsAt] = useState<{ section: 'profile' | 'password'; n: number }>({ section: 'profile', n: 0 });
  useEffect(() => {
    if (workRequest !== null) setTab('work');
  }, [workRequest]);

  /* Push on change, so back/forward walk the history the operator actually made. Replace on
   * first paint instead, so landing on `/` does not leave an empty entry behind them. */
  useEffect(() => {
    /* The cabinet tab and the cabinet being edited carry their subject; every other tab's query belongs to its view. */
    const carried = (tab === 'cabinet' && cabinetId !== null) || (tab === 'cabinet-onboarding' && editCabinetId !== null);
    const seg =
      tab === 'cabinet' && cabinetId !== null
        ? `/cabinet?id=${encodeURIComponent(cabinetId)}`
        : tab === 'cabinet-onboarding' && editCabinetId !== null
          ? `/cabinet-onboarding?edit=${encodeURIComponent(editCabinetId)}`
          : `/${tab}`;
    if ((carried ? window.location.pathname + window.location.search : window.location.pathname) === seg) return;
    const from = window.location.pathname.replace(/^\/+|\/+$/g, '');
    /*
     * A public route is REPLACED, not pushed past. An invitation URL is single-use: leaving
     * it in the history gives the operator a Back button that returns to a page asking them
     * to accept an invitation they have already accepted.
     */
    const entry = from === '' || (PUBLIC_ROUTES as readonly string[]).includes(from);
    window.history[entry ? 'replaceState' : 'pushState']({ tab }, '', seg);
  }, [tab, cabinetId, editCabinetId]);

  /* The browser's own navigation. Without this, back/forward change the URL and not the view —
   * which is worse than not supporting them, because the address bar then lies. */
  useEffect(() => {
    const onPop = (): void => {
      setTab(tabFromLocation());
      setCabinetId(cabinetFromLocation());
      setEditCabinetId(cabinetEditFromLocation());
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const counts = useDeviceCounts();
  const { selectedId, selected, select: selectSite } = useSiteContext();
  /*
   * UNRESOLVED FINDINGS, FOR THE RAIL'S BADGE — scoped to the selected site (Hamza,
   * 2026-09-09), like the alarm badge below it and unlike before. The badge counted the fleet
   * while the Findings page it opens on defaults to the site, and a "7" that opens a list of
   * two teaches the operator that the rail's numbers are not to be trusted.
   */
  const findings = useFindingsSummary(selectedId ?? undefined);
  /*
   * OPEN ALARMS, FOR THE RAIL'S OWN BADGE.
   *
   * Scoped to the selected site exactly as every other alarm surface is, so the rail agrees
   * with the Alarms view an operator reaches by clicking it. It deliberately does NOT reuse
   * the bell's number: the bell counts alarm EVENTS this browser has not looked at yet and
   * resets to zero when opened, which is a property of this tab. `open` is how many alarms
   * are currently raised at the plant — a property of the plant, true on every screen, and
   * the only one of the two that means anything in a navigation rail.
   */
  const alarmSummary = useAlarmSummary(selectedId ?? undefined);
  const openAlarms = alarmSummary.data?.open ?? 0;
  const alarmsCritical = (alarmSummary.data?.bySeverity.critical ?? 0) > 0;

  /*
   * The remote-control rail entry exists only for someone who can act on it. Both flags
   * arrive from /me already AND-ed with the organisation's `remote_control_enabled` gate,
   * so an org with the feature off simply has no entry — the honest default for a
   * capability whose default is OFF.
   */
  const me = useMe();
  const canProposeWrites = me.data?.can.proposeWrites ?? false;
  const canApproveWrites = me.data?.can.approveWrites ?? false;

  /*
   * WHAT THIS PERSON IS NOT OFFERED, and it was one id long.
   *
   * Until today this read `canProposeWrites || canApproveWrites ? [] : ['writes']` — a single
   * capability-gated destination — and nothing else in the rail consulted a permission at all.
   * Signing in as each of the four real roles returned the SAME 23 entries every time: an
   * Operator holding `alarms.triage` and `users.read` was offered Access, Audit trail, SLD
   * Studio, Onboarding and Device management, and every one of them answered 403.
   *
   * TWO SOURCES, JOINED HERE RATHER THAN MERGED, because they mean different things:
   *
   *   · `item.needs` is a PERMISSION. Absent means anybody signed in may look, which is most
   *     screens — see the long note on `NavItem.needs` for why reading the fleet, the alarm
   *     board or a sealed report is not gated.
   *   · `writes` is a permission AND an ORGANISATION gate (ADR-0014). Its entry stays on the
   *     line below so the distinction survives: "your role cannot" and "this organisation has
   *     it switched off" are different sentences, and `can.proposeWrites` has already AND-ed
   *     them, which a slug lookup here could not express.
   *
   * FAILS CLOSED WHILE `/me` IS IN FLIGHT. `me.data` is undefined on the first render, so the
   * set is empty and everything gated is hidden until the answer arrives. The alternative —
   * showing everything and removing entries a moment later — is a rail that flickers items an
   * operator may have already reached for.
   */
  const held = useMemo(() => new Set(me.data?.permissions ?? []), [me.data?.permissions]);
  const hiddenItems = useMemo(
    () => [
      /*
       * WHAT THIS PRODUCT DOES NOT OFFER, joined here so it rides the same two mechanisms a
       * missing permission does: the rail drops the entry and the effect below bounces a
       * bookmarked URL. A product can only ever hide a screen this way, never reveal one.
       */
      ...PRODUCT.hiddenTabs,
      ...NAV_GROUPS.flatMap((g) =>
        g.items
          .filter((i) => {
            const needs = needsOf(PRODUCT, i);
            return needs !== undefined && !held.has(needs);
          })
          .map((i) => i.id),
      ),
      ...(canProposeWrites || canApproveWrites ? [] : ['writes']),
    ],
    [held, canProposeWrites, canApproveWrites],
  );

  /*
   * AND A SCREEN THAT IS NO LONGER OFFERED MUST NOT STILL BE SHOWING.
   *
   * Hiding the rail entry is only half of it. `tab` is state, restored from the URL at
   * `tabFromLocation()`, so three routes reach a hidden screen with the entry already gone:
   * a bookmarked or shared link, a browser Back into one, and an administrator changing
   * somebody's role while they have it open — role is resolved PER REQUEST, so the next
   * `/me` answers with the new set and the rail loses the entry under them.
   *
   * In all three the view keeps rendering and every query inside it 403s, which reads as a
   * broken screen rather than as access they do not have. Falling back to `overview` is the
   * one destination no permission gates.
   *
   * Waits for `/me` to answer. Until then `hiddenItems` is everything gated — see the note
   * above — and acting on that would bounce a legitimate deep link off its own screen.
   */
  useEffect(() => {
    if (!me.data) return;
    if (hiddenItems.includes(tab)) setTab(DEFAULT_TAB);
  }, [me.data, hiddenItems, tab]);
  /*
   * The pending count is a QUEUE for approvers — each one is a colleague waiting on a
   * second signature against a 24 h clock — so it is badged like the device queue is.
   * Fetched only when this user can actually approve: for anyone else the number is not
   * actionable, and the request would be capability-refused anyway.
   */
  const writeQueue = useWriteRequests({ status: 'pending', limit: 1 }, canApproveWrites);
  const pendingWrites = canApproveWrites ? (writeQueue.data?.pending ?? 0) : 0;

  // Compliance evaluates the SELECTED site now, not an archetype template. The template
  // switcher that stood in for this scored a site nobody owns.
  const site = useSite(tab === 'compliance' ? selectedId : null);

  const pending = counts.data?.counts.pending ?? 0;

  /*
   * THE LIVE ALARM STREAM, opened once for the whole app.
   *
   * Here rather than inside `AlarmBell`, because the connection is per PROCESS and not per
   * component: two components calling `useAlarmStream` would open two streams and double
   * every toast. The bell and the toast host both read the store this fills.
   *
   * `enabled: true` unconditionally, because this component only renders behind the auth
   * gate — and the hook re-checks the session on every connect anyway, reporting `offline`
   * rather than retrying into a 401 loop if it has gone.
   */
  const pushAlarm = useAlarmFeed((s) => s.push);
  const setStream = useAlarmFeed((s) => s.setStream);
  const streamState = useAlarmStream({ onAlarm: pushAlarm, enabled: true });
  useEffect(() => {
    setStream(streamState);
  }, [streamState, setStream]);

  /** Sub-views of Sites keep the rail on Sites. */
  /* `metrics` is a subpage of Device management, so the rail must highlight its parent —
     otherwise a bookmarked /metrics shows no active entry at all. */
  const railTab =
    tab === 'site-detail' ? 'sites' : tab === 'metrics' ? 'devices' : tab;

  /* The rail's counts, and Home's: one object, so a tile and a rail entry can never disagree. */
  const badges: Record<string, { count: number; tone: 'warn' | 'danger' } | undefined> = {
    devices: pending > 0 ? { count: pending, tone: 'warn' } : undefined,
    writes: pendingWrites > 0 ? { count: pendingWrites, tone: 'warn' } : undefined,
    /* Danger only when something critical is raised. A rail where every badge is red
       teaches an operator to ignore red. */
    alarms:
      openAlarms > 0
        ? { count: openAlarms, tone: alarmsCritical ? 'danger' : 'warn' }
        : undefined,
  };

  return (
    <Shell
      brandSlot={<CustomerBrand />}
      tab={railTab}
      onTab={(id) => setTab(id as Tab)}
      findingCount={findings.data?.unresolved ?? 0}
      badges={badges}
      hiddenItems={hiddenItems}
      headerSlot={
        <>
          {/*
            * ONE BUTTON, ONE POPOVER — not three bordered controls, and not the breadcrumb
            * that replaced them first (rejected: too wide, three carets, everything at equal
            * weight). `HeaderScope` owns organisation, site and plant behind a single control;
            * see that file for the accessibility contract a popover has to rebuild that a
            * native `<select>` gave away for free.
            */}
          <HeaderScope scope={siteScopeOf(tab)} plantInScope={plantScopeOf(tab)} />
          {/*
            * THE POSTURE ROW — Alarms · Findings · Compliance, ORDERED BY HOW SOON EACH NEEDS
            * YOU. Alarms are live and can be firing right now; findings are derived gaps that
            * do not change on their own between visits; compliance is the standing score they
            * both feed. The bell used to sit after the compliance chip — it now leads the row,
            * and findings (previously absent from the header entirely, though the rail has
            * carried its count since Findings shipped) sits between the two. The account menu
            * is unaffected: it is rendered last by the Shell itself (`accountSlot`), so
            * nothing added here can displace it from the corner.
            *
            * FINDINGS AND COMPLIANCE ARE ICON BUTTONS WITH A DOT, NOT PILLS, as of this
            * change. The two used to spell their numbers out inline (`⚠ 2 findings`,
            * `● 83% partial | 2 open`) — together with `HeaderScope` and the bell that was
            * four controls' worth of width for what a 32px icon now covers for two of them,
            * ~250px reclaimed. The numbers did not go away; they moved into a popover behind
            * each icon (`FindingsButton`, `ComplianceButton`), and only the alarm bell still
            * carries a number rather than a dot — see `HeaderStatButton`'s file comment for
            * why a count is worth the header's space for alarms and for neither of the other
            * two. Three icons, three different silhouettes (bell / triangle / shield) and
            * only the bell may glow: see the same file for both rules.
            */}
          {/* CRATUS 2.0 #19: what is assigned to me, on every screen. It opens the board on the
              alarm's device, where its triage is. */}
          <MyWork onOpen={(a) => openAlarmsFor(a.deviceId !== null ? { deviceId: a.deviceId } : {})} />
          {/* Live alerts and the watchlist drop down under their buttons (the FESCO design's header
              panels), so each button and its panel share a positioned box. */}
          {__PRODUCT__ === 'cratus' && (
            <div className="relative">
              <LiveAlertsButton open={liveOpen} onToggle={() => setLiveOpen((v) => !v)} />
              {liveOpen && (
                <Suspense fallback={null}>
                  <LiveAlertsDrawer
                    onClose={() => setLiveOpen(false)}
                    onOpenCabinet={(id, sensor) => openCabinet(id, sensor)}
                    onOpenAlarms={() => openAlarmsFor({})}
                  />
                </Suspense>
              )}
            </div>
          )}
          {__PRODUCT__ === 'cratus' && <SearchButton onOpen={() => setSearchOpen(true)} />}
          {searchOpen && (
            <Suspense fallback={null}>
              <SearchPalette onClose={() => setSearchOpen(false)} onOpen={openSearchResult} />
            </Suspense>
          )}
          {/* What this person follows (Lumen-BE 0095, roadmap §11.5): every screen, like Live alerts. */}
          {__PRODUCT__ === 'cratus' && (
            <div className="relative">
              <WatchlistButton open={watchOpen} onToggle={() => setWatchOpen((v) => !v)} />
              {watchOpen && (
                <Suspense fallback={null}>
                  <WatchlistDrawer
                    onClose={() => setWatchOpen(false)}
                    onOpenCabinet={(id) => openCabinet(id)}
                    onOpenSite={(id) => {
                      selectSite(id);
                      setTab('site-detail');
                    }}
                    onOpenAlarms={() => openAlarmsFor({ queue: 'watched' })}
                  />
                </Suspense>
              )}
            </div>
          )}
          <AlarmBell />
          {/* Only where the screen they open exists — Cratus has no Comply group. */}
          {!PRODUCT.hiddenTabs.has('findings') && (
            <FindingsButton
              summary={findings.data}
              loading={findings.isLoading}
              siteName={selected?.name}
              onOpen={() => setTab('findings')}
            />
          )}
          {!PRODUCT.hiddenTabs.has('compliance') && (
            <ComplianceButton onOpen={() => setTab('compliance')} />
          )}
        </>
      }
      accountSlot={
        __PRODUCT__ === 'cratus' ? (
          <Suspense fallback={<AccountMenu />}>
            <AccountMenuFesco
              onOpenSettings={(section) => {
                setSettingsAt((v) => ({ section, n: v.n + 1 }));
                setTab('profile');
              }}
              onOpenAppearance={hiddenItems.includes('appearance') ? null : () => setTab('appearance')}
            />
          </Suspense>
        ) : (
          <AccountMenu />
        )
      }
    >
      {/*
        * The toast host, INSIDE the Shell but outside the error boundary.
        *
        * Outside on purpose: a crashed view must not take the alarm notifications with it.
        * A screen that has thrown is exactly when an operator most needs to be told the
        * plant is doing something, and a toast host inside the boundary would be unmounted
        * by the very failure they need to work around.
        */}
      <AlarmToasts />
      {/*
        * And the ACTION toasts — `toast.success` / `toast.error` from every form in the console.
        * They were only ever mounted on the sign-in screens, so inside the shell every one of
        * them went into the store and nowhere else: a refused invitation (422) closed nothing,
        * said nothing, and looked like a button that does not work. Top right; the alarm stack
        * is bottom right, so the two never overlap.
        */}
      <Toasts />

      {/*
        * Inside the Shell, around the routed view only: the sidebar and header stay alive
        * so a crashed screen is something to navigate away from rather than a white page.
        * Keyed on `tab` so moving to another module clears the error.
        */}
      <ViewErrorBoundary resetKey={tab} label={NAV_LABEL[tab] ?? tab}>
      <Suspense fallback={<ViewLoading label={NAV_LABEL[tab] ?? tab} />}>
      {tab === 'home' ? (
        <HomeView hiddenItems={hiddenItems} badges={badges} onTab={(id) => setTab(id as Tab)} onOpenCabinet={(id) => openCabinet(id)} />
      ) : tab === 'command' ? (
        <CommandCentreView
          onOpenAlarms={openAlarmsFor}
          onOpenCabinet={openCabinet}
          onOpenSite={(siteId) => {
            selectSite(siteId);
            setTab('site-detail');
          }}
        />
      ) : tab === 'overview' ? (
        <OverviewView onOpenAlarms={openAlarmsFor} onOpenCompliance={openComplianceFor} />
      ) : tab === 'sites' ? (
        <SitesView
          /* Cratus has no site wizard: a site arrives with its first cabinet. */
          onOnboard={() => {
            setEditCabinetId(null);
            setTab(PRODUCT.hiddenTabs.has('onboarding') ? 'cabinet-onboarding' : 'onboarding');
          }}
          onOpenDevices={() => setTab('devices')}
          onOpenSite={() => setTab('site-detail')}
        />
      ) : (tab === 'site-detail' || tab === 'site-center') && selectedId ? (
        /*
         * The site HUB, not the old single-page detail view.
         *
         * `SiteHubView` is the multi-page site workspace — SLD, Parameters, Alarms, Analytics,
         * Devices, Detail — and it was built but never routed, so clicking a site landed on
         * `SiteDetailView` instead and none of those pages was reachable from anywhere in the
         * product. `SiteDetailView` is now unused; it stays in the tree only until its
         * remaining copy is folded into the hub's Detail tab.
         */
        <SiteHubView
          siteId={selectedId}
          {...(__PRODUCT__ === 'cratus' ? { onSelectSite: selectSite } : {})}
          onBack={() => setTab('sites')}
          onOpenDeviceQueue={() => setTab('devices')}
          onOpenFindings={() => setTab('findings')}
          {...(PRODUCT.hiddenTabs.has('cabinet') ? {} : { onOpenCabinet: (id: string, sensor?: string | null) => openCabinet(id, sensor ?? null) })}
          {...(PRODUCT.hiddenTabs.has('analysis') ? {} : { onOpenAnalysis: openAnalysis })}
        />
      ) : tab === 'site-center' ? (
        /* Single Site Center with no site chosen yet — the header's selector or Sites picks one. */
        <Page title="Single Site Center" desc="Choose a site to open it here.">
          <div className="lm-card rounded-[var(--radius-md)] p-[28px] text-center">
            <p className="lm-t-sm" style={{ color: 'var(--muted-fg)' }}>
              No site is selected. Pick one in the header, or open the list of sites.
            </p>
            <button
              type="button"
              onClick={() => setTab('sites')}
              className="lm-press mt-[12px] rounded-[var(--radius-sm)] px-[14px] py-[7px] text-[13px] font-semibold"
              style={{ background: 'var(--brand)', color: 'var(--brand-fg)' }}
            >
              Open Sites
            </button>
          </div>
        </Page>
      ) : tab === 'onboarding' ? (
        <SiteOnboardingView
          onFinish={() => setTab('sites')}
          onOpenDevices={() => setTab('devices')}
        />
      ) : tab === 'cabinets' ? (
        <CabinetsView
          justOnboarded={justOnboarded}
          onOnboard={() => {
            setJustOnboarded(null);
            setEditCabinetId(null);
            setTab('cabinet-onboarding');
          }}
          onEdit={(id) => {
            setJustOnboarded(null);
            setEditCabinetId(id);
            setTab('cabinet-onboarding');
          }}
          onOpenSite={(siteId) => {
            selectSite(siteId);
            setTab('site-detail');
          }}
          onOpenCabinet={openCabinet}
        />
      ) : tab === 'cabinet' ? (
        <CabinetView
          cabinetId={cabinetId}
          initialSensor={cabinetSensor}
          onSensorApplied={() => setCabinetSensor(null)}
          onPick={setCabinetId}
          onOpenAlarms={openAlarmsFor}
          onOpenSite={(siteId) => {
            selectSite(siteId);
            setTab('site-detail');
          }}
          onOpenCabinets={() => setTab('cabinets')}
          {...(PRODUCT.hiddenTabs.has('analysis') ? {} : { onAnalyse: (id: string) => openAnalysis([id]) })}
        />
      ) : tab === 'cabinet-onboarding' ? (
        <CabinetOnboardingView
          /* Keyed on what it edits, so moving from one cabinet's edit to a new onboarding starts clean. */
          key={editCabinetId ?? 'new'}
          editId={editCabinetId}
          onDone={(created) => {
            setJustOnboarded(created?.serialNumber ?? null);
            setEditCabinetId(null);
            setTab('cabinets');
          }}
        />
      ) : tab === 'integrations' ? (
        <IntegrationsView />
      ) : tab === 'devices' ? (
        <DeviceManagementView />
      ) : tab === 'findings' ? (
        <FindingsView {...(selectedId ? { siteId: selectedId } : {})} />
      ) : tab === 'standards' ? (
        <StandardsView />
      ) : tab === 'calculators' ? (
        <CalculatorsView />
      ) : tab === 'writes' ? (
        <RemoteControlView />
      ) : tab === 'reports' ? (
        <ReportsView />
      ) : tab === 'users' ? (
        <UsersView onOpenAudit={openAuditFor} onOpenProfile={() => setTab('profile')} />
      ) : tab === 'performance' ? (
        <PerformanceView />
      ) : tab === 'access' ? (
        <AccessView />
      ) : tab === 'audit' ? (
        <AuditView
          {...(auditFocus ? { focus: auditFocus } : {})}
          onFocusApplied={() => setAuditFocus(null)}
          onOpenUsers={() => setTab('users')}
        />
      ) : tab === 'organisation' ? (
        <OrganisationView
          onOpenUsers={() => setTab('users')}
          onOpenDevices={() => setTab('devices')}
          /* Cratus's Access is Profile Management (one screen, the FESCO design). */
          onOpenAccess={() => setTab(PRODUCT.hiddenTabs.has('access') ? 'profiles' : 'access')}
        />
      ) : tab === 'profile' ? (
        __PRODUCT__ === 'cratus' ? (
          <SettingsView key={settingsAt.n} initialSection={settingsAt.section} onOpenAudit={openAuditFor} />
        ) : (
          <ProfileView onOpenAudit={openAuditFor} onOpenUsers={() => setTab('users')} />
        )
      ) : tab === 'profiles' ? (
        <ProfileManagementView />
      ) : tab === 'appearance' ? (
        <AppearanceView hiddenItems={hiddenItems} />
      ) : tab === 'design' ? (
        <DesignSystemView />
      ) : tab === 'twin' ? (
        <DigitalTwinView onOpenAlarms={() => setTab('alarms')} />
      ) : tab === 'topology' ? (
        <SldStudioView {...(selectedId ? { siteId: selectedId } : {})} />
      ) : tab === 'metrics' ? (
        /* The old rail entry. Same screen, reached through its container so the sub-nav is
           present — a bookmark should not land somewhere with no way back to its siblings. */
        <DeviceManagementView />
      ) : tab === 'analysis' ? (
        <AnalysisView
          initialCabinetIds={analysisCabinets}
          onInitialApplied={() => setAnalysisCabinets(null)}
          onOpenAlarms={openAlarmsFor}
        />
      ) : tab === 'work' ? (
        <WorkView
          initialWorkId={workRequest?.kind === 'open' ? workRequest.id : workLink}
          initialDraft={workRequest?.kind === 'draft' ? workRequest.draft : null}
          initialQueue={workRequest?.kind === 'queue' ? workRequest.queue : null}
          initialSchedules={workRequest?.kind === 'schedules'}
          onInitialApplied={() => {
            setWorkLink(null);
            useWorkNav.getState().clear();
          }}
          onOpenCabinet={(id) => openCabinet(id)}
          onOpenAlarm={(id) => {
            setAlarmLink(id);
            setTab('alarms');
          }}
        />
      ) : tab === 'fleet-reports' ? (
        <FleetReportsView />
      ) : tab === 'assets' ? (
        <AssetsView onOpenCabinet={(id) => openCabinet(id)} />
      ) : tab === 'telemetry' ? (
        <TelemetryView onOpenDevices={() => setTab('devices')} />
      ) : tab === 'alarms' ? (
        <AlarmsView
          {...(alarmFocus?.deviceId !== undefined ? { focusDeviceId: alarmFocus.deviceId } : {})}
          {...(alarmFocus?.queue !== undefined ? { focusQueue: alarmFocus.queue } : {})}
          {...(alarmLink !== null ? { focusAlarmId: alarmLink } : {})}
          onFocusApplied={() => {
            setAlarmFocus(null);
            setAlarmLink(null);
          }}
          {...(PRODUCT.hiddenTabs.has('cabinet') ? {} : { onOpenCabinet: openCabinet })}
        />
      ) : tab === 'compliance' ? (
        site.data ? (
          <ComplianceStage
            siteId={site.data.site.id}
            siteName={selected?.name ?? 'Site'}
            params={site.data.site.params}
          />
        ) : (
          <Page title="Compliance" desc="Loading the selected site…">
            <div className="lm-sk h-64 rounded-[var(--radius)]" />
          </Page>
        )
      ) : (
        <Stub tab={tab} />
      )}
      </Suspense>
      </ViewErrorBoundary>
    </Shell>
  );
}

/**
 * Nothing is stubbed any more — every entry in NAV_GROUPS routes to a real view.
 *
 * `Stub` is kept as the fallback for an unroutable tab, which is now only reachable via a
 * bad `setTab` or a nav id added without a route. Deleting it would turn that mistake into
 * a blank pane with no explanation; keeping it names the tab that has no home.
 */

/**
 * What fills the view area while its chunk arrives: the product's animated mark (`BrandLoader`).
 *
 * IN THE PAGE, NOT OVER THE CONSOLE, and it keeps the title. The shell, rail and header are
 * already painted, so the loader says that this page is loading, not the app. The title comes
 * from the nav label they just clicked, so the header does not blank and return. (This was a
 * grey skeleton until 2026-10-02, when every page was asked to load with the logo.)
 *
 * Only ever seen on a cold chunk: once a view has been opened, its chunk is cached and
 * the switch is synchronous.
 */
function ViewLoading({ label }: { label: string }): ReactNode {
  return (
    <Page title={label} desc="Loading…">
      <div className="grid place-items-center" style={{ minHeight: '46vh' }}>
        <BrandLoader size={44} label={`Loading ${label}`} />
      </div>
    </Page>
  );
}

function Stub({ tab }: { tab: string }): ReactNode {
  const copy = { title: NAV_LABEL[tab] ?? tab, desc: 'This tab has no view wired to it.' };
  return (
    <Page title={copy.title} desc={copy.desc}>
      <div className="lm-card flex items-start gap-[10px] p-[18px_20px]">
        <span
          className="flex size-9 shrink-0 items-center justify-center rounded-[9px]"
          style={{ background: 'var(--secondary)' }}
        >
          <Icon name="ic-info" size={17} />
        </span>
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-[14px] font-bold">Not yet implemented</span>
            <StatusBadge verdict="info" label="Planned" />
          </div>
          <p className="text-[13px] leading-[1.55]" style={{ color: 'var(--muted-fg)' }}>
            See <span className="lm-num">Docs/07-delivery/ROADMAP.md</span> for what ships
            when. The compliance engine these modules consume is already live and scoring
            the selected site — see Compliance, Standards and Findings.
          </p>
        </div>
      </div>
    </Page>
  );
}
